/**
 * FigureLabelStep
 *
 * Shown during positional template creation after person detection.
 * Coach selects 2–5 detected persons and assigns a short position_label to each
 * (e.g. "PG", "SG", "C", "LW").
 *
 * Validation rules (mirror PTD-2 backend):
 *  - 2–5 figures must be selected.
 *  - Each label must be non-empty and 1–10 characters.
 *  - Labels must be unique across all figures.
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Alert, Button } from '@/components/ui';
import type { PersonInfo } from '@/types/api';
import {
  type FigureSelection,
  MIN_FIGURES,
  MAX_FIGURES,
  MAX_LABEL_LEN,
  validateSelections,
} from './figureSelectionValidation';

interface FigureLabelStepProps {
  persons: PersonInfo[];
  initialSelections?: FigureSelection[];
  onConfirm: (figures: FigureSelection[]) => void;
  onBack: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Per-person accent color set — mirrors PersonPicker palette.
const PERSON_COLORS = [
  { border: 'border-blue-400',   bg: 'bg-blue-400/10',   text: 'text-blue-300'   },
  { border: 'border-green-400',  bg: 'bg-green-400/10',  text: 'text-green-300'  },
  { border: 'border-orange-400', bg: 'bg-orange-400/10', text: 'text-orange-300' },
  { border: 'border-purple-400', bg: 'bg-purple-400/10', text: 'text-purple-300' },
  { border: 'border-pink-400',   bg: 'bg-pink-400/10',   text: 'text-pink-300'   },
];

function colorFor(index: number) {
  return PERSON_COLORS[index % PERSON_COLORS.length];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function FigureLabelStep({ persons, initialSelections, onConfirm, onBack }: FigureLabelStepProps) {
  // selected person indices
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    () => new Set(initialSelections?.map((s) => s.person_index) ?? []),
  );

  // label per person_index
  const [labels, setLabels] = useState<Record<number, string>>(
    () => Object.fromEntries(initialSelections?.map((s) => [s.person_index, s.position_label]) ?? []),
  );

  const [error, setError] = useState<string | null>(null);

  // When persons list changes (e.g. hot-reload), re-apply initial
  useEffect(() => {
    if (initialSelections && initialSelections.length > 0) {
      setSelectedIndices(new Set(initialSelections.map((s) => s.person_index)));
      setLabels(Object.fromEntries(initialSelections.map((s) => [s.person_index, s.position_label])));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount — `initialSelections` is stable at mount time.

  function togglePerson(personIndex: number) {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(personIndex)) {
        next.delete(personIndex);
      } else {
        next.add(personIndex);
      }
      return next;
    });
    setError(null);
  }

  function setLabel(personIndex: number, value: string) {
    setLabels((prev) => ({ ...prev, [personIndex]: value }));
    setError(null);
  }

  function handleConfirm() {
    const selections: FigureSelection[] = [...selectedIndices].sort().map((idx) => {
      const person = persons.find((p) => p.person_index === idx);
      return {
        person_index: idx,
        position_label: (labels[idx] ?? '').trim(),
        crop_data_url: person?.crop_data_url,
      };
    });

    const validationError = validateSelections(selections);
    if (validationError) {
      setError(validationError);
      return;
    }

    onConfirm(selections);
  }

  const selectionCount = selectedIndices.size;
  const selectionStatus =
    selectionCount < MIN_FIGURES
      ? `Select ${MIN_FIGURES - selectionCount} more person${MIN_FIGURES - selectionCount !== 1 ? 's' : ''}`
      : selectionCount > MAX_FIGURES
        ? `Too many selected — remove ${selectionCount - MAX_FIGURES}`
        : `${selectionCount} figure${selectionCount !== 1 ? 's' : ''} selected`;

  const canContinue = selectionCount >= MIN_FIGURES && selectionCount <= MAX_FIGURES;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Label Figures</CardTitle>
        <p className="text-sm text-text-secondary mt-1">
          Select {MIN_FIGURES}–{MAX_FIGURES} performers from the video and give each a short position
          label (e.g. "PG", "SF", "GK"). Labels identify each role during evaluation.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Person grid */}
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}
        >
          {persons.map((person) => {
            const isSelected = selectedIndices.has(person.person_index);
            const color = colorFor(person.person_index);
            const labelValue = labels[person.person_index] ?? '';
            const labelTooLong = labelValue.length > MAX_LABEL_LEN;

            return (
              <div
                key={person.person_index}
                className={[
                  'flex flex-col rounded-lg border-2 transition-colors overflow-hidden',
                  isSelected
                    ? `${color.border} ${color.bg}`
                    : 'border-border-subtle bg-dark-elevated-1 hover:border-border-strong',
                ].join(' ')}
              >
                {/* Thumbnail / placeholder */}
                <button
                  type="button"
                  aria-label={`${isSelected ? 'Deselect' : 'Select'} Person ${person.person_index + 1}`}
                  aria-pressed={isSelected}
                  onClick={() => togglePerson(person.person_index)}
                  className="relative w-full aspect-[3/4] focus:outline-none"
                >
                  {person.crop_data_url ? (
                    <img
                      src={person.crop_data_url}
                      alt={`Person ${person.person_index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-dark-elevated-2">
                      <span className="text-3xl font-bold text-text-muted">
                        {person.person_index + 1}
                      </span>
                    </div>
                  )}
                  {/* Checkbox badge */}
                  <div
                    className={[
                      'absolute top-2 right-2 h-5 w-5 rounded-full border-2 flex items-center justify-center',
                      isSelected
                        ? `${color.border} bg-accent`
                        : 'border-white/60 bg-black/40',
                    ].join(' ')}
                  >
                    {isSelected && (
                      <svg
                        className="h-3 w-3 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={3}
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  {/* Person number badge */}
                  <div
                    className={[
                      'absolute top-2 left-2 h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold text-white',
                      'bg-black/60',
                    ].join(' ')}
                  >
                    {person.person_index + 1}
                  </div>
                </button>

                {/* Label input — only shown when selected */}
                <div className="p-2">
                  <label
                    htmlFor={`position-label-${person.person_index}`}
                    className={`block text-xs font-medium mb-1 ${color.text}`}
                  >
                    {isSelected ? 'Position label' : `Person ${person.person_index + 1}`}
                  </label>
                  {isSelected ? (
                    <input
                      id={`position-label-${person.person_index}`}
                      type="text"
                      value={labelValue}
                      onChange={(e) => setLabel(person.person_index, e.target.value)}
                      placeholder="e.g. PG"
                      maxLength={MAX_LABEL_LEN + 1} // +1 so user sees the error before being cut off
                      aria-label={`Position label for Person ${person.person_index + 1}`}
                      className={[
                        'w-full px-2 py-1 text-sm rounded border bg-dark-elevated-2 text-text-primary',
                        'placeholder:text-text-muted focus:outline-none focus:border-accent',
                        labelTooLong ? 'border-danger' : 'border-border-subtle',
                      ].join(' ')}
                    />
                  ) : (
                    <p className="text-xs text-text-muted italic">Click to select</p>
                  )}
                  {labelTooLong && (
                    <p className="text-xs text-danger mt-0.5">
                      Max {MAX_LABEL_LEN} characters
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Selection counter */}
        <p
          className={[
            'text-sm font-medium',
            canContinue ? 'text-success' : 'text-text-muted',
          ].join(' ')}
          aria-live="polite"
        >
          {selectionStatus}
        </p>

        {error && <Alert variant="error">{error}</Alert>}

        <div className="flex gap-3 pt-2">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!canContinue}
            className="flex-1"
          >
            Continue — Annotate Each Figure
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default FigureLabelStep;
// validateSelections, FigureSelection, MIN_FIGURES, MAX_FIGURES, MAX_LABEL_LEN
// moved to figureSelectionValidation.ts (react-refresh/only-export-components).
